import React, { useState, useEffect } from 'react';
import { dartThemes } from '../dartThemes';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

export const MembersPage = ({ userRole, navigate }) => {
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAllMembers, setShowAllMembers] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Players
                const usersCollectionRef = collection(db, "users");
                const usersSnapshot = await getDocs(usersCollectionRef);
                const playersList = usersSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    .filter(player => !player.archived); // Filter for active roster players only
                setPlayers(playersList);

                // Fetch Teams
                const teamsCollectionRef = collection(db, "teams");
                const teamsSnapshot = await getDocs(teamsCollectionRef);
                const teamsList = teamsSnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(team => !team.archived); // Filter for active teams only
                setTeams(teamsList);

            } catch (error) {
                logError("Error fetching data: ", error);
            }
            setLoading(false);
        };

        fetchData();
    }, []);

    if (!userRole) {
        return <p className="text-xl text-center text-danger">Please log in to view members and teams.</p>;
    }

    const getPlayerDisplayName = (player) => {
        return player.nickname || `${player.firstName} ${player.lastName}`;
    };

    const getPlayerEmail = (player) => {
        return (
            player.email ||
            player.emailAddress ||
            player.contactEmail ||
            (player.contact && player.contact.email) ||
            null
        );
    };

    // Removed unused phone display helper to satisfy eslint no-unused-vars

    const getTelHref = (phone) => {
        if (!phone) return null;
        if (/^\+\d{8,15}$/.test(phone)) return `tel:${phone}`;
        const digits = ('' + phone).replace(/\D/g, '');
        if (digits.length === 10) return `tel:+1${digits}`;
        if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
        return null;
    };

    const getSmsHref = (phone) => {
        if (!phone) return null;
        if (/^\+\d{8,15}$/.test(phone)) return `sms:${phone}`;
        const digits = ('' + phone).replace(/\D/g, '');
        if (digits.length === 10) return `sms:+1${digits}`;
        if (digits.length === 11 && digits.startsWith('1')) return `sms:+${digits}`;
        return null;
    };

    if (loading) {
        return <div className="text-center text-muted">Loading members...</div>;
    }

    return (
        <div className="space-y-4">
            <h2 className="text-4xl font-extrabold text-center heading-primary-1 mb-6 transition-colors duration-300">League Members & Teams</h2>

            <div className="flex justify-center space-x-4 mb-6">
                <button
                    onClick={() => setShowAllMembers(true)}
                    className={`segmented-btn py-2 px-6 rounded-lg font-bold transition duration-200 ${showAllMembers ? 'segmented-btn-active' : ''}`}
                >
                    Show All Members
                </button>
                <button
                    onClick={() => setShowAllMembers(false)}
                    className={`segmented-btn py-2 px-6 rounded-lg font-bold transition duration-200 ${!showAllMembers ? 'segmented-btn-active' : ''}`}
                >
                    Show Teams
                </button>
            </div>

            {showAllMembers ? (
                // Display All Members
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {players.map(player => (
                        <div key={player.id} className="card rounded-lg shadow-md p-4 flex flex-col items-center justify-center space-y-2 transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
                            <div className="surface-low w-20 h-20 rounded-full flex items-center justify-center overflow-hidden shadow-inner">
                                {dartThemes[player.profileImage] || dartThemes['default']}
                            </div>
                            <p className="text-lg font-semibold text-strong">
                                <span>{getPlayerDisplayName(player)}</span>
                                {!(player.role === 'member' || player.role === 'admin') && (
                                    <span className="ml-2 text-sm text-muted">(non-member)</span>
                                )}
                            </p>
                            <div className="flex flex-col gap-2 mt-2 w-full">
                                {(() => {
                                    const email = getPlayerEmail(player);
                                    const emailHref = email ? `mailto:${email}?subject=Chico%20Darts%20Contact` : undefined;
                                    const telHref = getTelHref(player.phoneNumber) || undefined;
                                    const smsHref = getSmsHref(player.phoneNumber) || undefined;

                                    const emailEnabled = Boolean(emailHref);
                                    const callEnabled = Boolean(telHref);
                                    const textEnabled = Boolean(smsHref);

                                    return (
                                        <>
                                <a
                                    href={emailHref}
                                    aria-disabled={!emailEnabled}
                                    tabIndex={emailEnabled ? 0 : -1}
                                    className={`btn w-full text-sm ${emailEnabled ? 'btn-primary' : 'btn-secondary btn-disabled'}`}
                                    onClick={(e) => { if (!emailEnabled) e.preventDefault(); }}
                                >
                                    Email
                                </a>
                                <a
                                    href={telHref}
                                    aria-disabled={!callEnabled}
                                    tabIndex={callEnabled ? 0 : -1}
                                    className={`btn w-full text-sm ${callEnabled ? 'btn-success' : 'btn-secondary btn-disabled'}`}
                                    onClick={(e) => { if (!callEnabled) e.preventDefault(); }}
                                >
                                    Call
                                </a>
                                <a
                                    href={smsHref}
                                    aria-disabled={!textEnabled}
                                    tabIndex={textEnabled ? 0 : -1}
                                    className={`btn w-full text-sm ${textEnabled ? 'btn-secondary' : 'btn-secondary btn-disabled'}`}
                                    onClick={(e) => { if (!textEnabled) e.preventDefault(); }}
                                >
                                    Text
                                </a>
                                <button
                                    type="button"
                                    onClick={() => navigate && navigate('Member Profile', { memberId: player.id })}
                                    className="btn btn-secondary w-full text-sm"
                                >
                                    View Profile
                                </button>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                // Display Teams
                <div className="space-y-6">
                    {teams.map(team => {
                        const hasMembers = Array.isArray(team.members) && team.members.length > 0;
                        const captainId = hasMembers ? team.members[0] : null;
                        const captain = captainId ? players.find(p => p.id === captainId) : null;
                        return (
                            <div key={team.id} className="card rounded-lg shadow-md p-6 transition-colors duration-300">
                                <h3 className="text-2xl font-semibold heading-primary-1 mb-3">{team.name}</h3>
                                <p className="text-lg font-medium text-strong mb-2">Team Members:</p>
                                <ul className="list-disc list-inside space-y-1 text-muted">
                                    {hasMembers ? (
                                        team.members.map((memberId, index) => {
                                            const player = players.find(p => p.id === memberId);
                                            return (
                                                <li key={memberId}>
                                                    {player ? (
                                                        <span>
                                                            {getPlayerDisplayName(player)}{index === 0 && <span className="ml-2 text-xs font-bold text-warning" >(Captain)</span>}
                                                        </span>
                                                    ) : (
                                                        `Unknown Player (ID: ${memberId})`
                                                    )}
                                                </li>
                                            );
                                        })
                                    ) : (
                                        <li>No members assigned to this team yet.</li>
                                    )}
                                </ul>
                                {captain && (
                                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                        {(() => {
                                            const captainEmail = getPlayerEmail(captain);
                                            const emailHref = captainEmail ? `mailto:${captainEmail}?subject=Chico%20Darts%20Team%20Captain` : undefined;
                                            const smsHref = getSmsHref(captain.phoneNumber) || undefined;

                                            const emailEnabled = Boolean(emailHref);
                                            const textEnabled = Boolean(smsHref);

                                            return (
                                                <>
                                        <a
                                            href={emailHref}
                                            aria-disabled={!emailEnabled}
                                            tabIndex={emailEnabled ? 0 : -1}
                                            className={`btn text-sm ${emailEnabled ? 'btn-secondary' : 'btn-secondary btn-disabled'}`}
                                            onClick={(e) => { if (!emailEnabled) e.preventDefault(); }}
                                        >
                                            Email Captain
                                        </a>
                                        <a
                                            href={smsHref}
                                            aria-disabled={!textEnabled}
                                            tabIndex={textEnabled ? 0 : -1}
                                            className={`btn text-sm ${textEnabled ? 'btn-secondary' : 'btn-secondary btn-disabled'}`}
                                            onClick={(e) => { if (!textEnabled) e.preventDefault(); }}
                                        >
                                            Text Captain
                                        </a>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};